import { useEffect, useState } from 'react';

const pressedButtons = new Set<number>();
const listeners: Array<() => void> = [];

/**
 * Sets up global pointer event listeners to track currently pressed buttons.
 */
function setupPointerListeners() {
    if (typeof window === 'undefined') {
        return;
    }

    function downHandler(event: PointerEvent) {
        pressedButtons.add(event.button);
        listeners.forEach((listener) => listener());
    }

    const upHandler = (event: PointerEvent) => {
        pressedButtons.delete(event.button);
        listeners.forEach((listener) => listener());
    };

    const cancelHandler = () => {
        pressedButtons.clear();
        listeners.forEach((listener) => listener());
    };

    window.addEventListener('pointerdown', downHandler, { capture: true });
    window.addEventListener('pointerup', upHandler, { capture: true });
    window.addEventListener('pointercancel', cancelHandler, { capture: true });
}

/**
 * Adds a listener that is called whenever the set of pressed buttons changes.
 */
function addPressedButtonListener(listener: () => void) {
    listeners.push(listener);
    return () => {
        const index = listeners.indexOf(listener);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    };
}

setupPointerListeners();

/**
 * Function to check if a specific button is currently pressed.
 */
export function checkIsButtonPressed(button: number): boolean {
    return pressedButtons.has(button);
}

/**
 * Hook that tracks if a specific button is currently pressed.
 */
export function useIsButtonPressed(button: number): boolean {
    const [pressed, setPressed] = useState(() => checkIsButtonPressed(button));
    useEffect(
        () => addPressedButtonListener(() => setPressed(checkIsButtonPressed(button))),
        [button]
    );
    return pressed;
}
